import React from "react";
import Tooltip from "@mui/material/Tooltip";
import classNames from "classnames";

import { InfoTooltipProps } from "./InfoTooltip.types";
import { useStyles, useTooltipStyles } from "./InfoTooltip.styles";
import Button from "../Button";
import Typography from "../Typography";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Info from "@tiny/icons/generated/Info";
import Info2Outline from "@tiny/icons/generated/Info2Outline";
import ShortRight from "@tiny/icons/generated/ShortRight";

function InfoTooltip(props: InfoTooltipProps): JSX.Element {
  const {
    title: titleProp,
    icon,
    focusIcon,
    modifiers,
    size,
    buttonText = "Got it",
    onButtonClick,
    className,
    placement = "bottom",
  } = props;

  const tooltipClasses = useTooltipStyles(props);

  const classes = useStyles(props);

  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const [open, setOpen] = React.useState(false);

  const refLabel = React.createRef<any>();

  const toolTipIcon = open && focusIcon ? focusIcon : icon;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    onButtonClick && onButtonClick();
  };

  const handleCloseAway = (event: any) => {
    !refLabel?.current?.contains(event.target) && setOpen(false);
  };

  const handleKeyDownClose = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (
      event.key === "Enter" ||
      event.key === "Tab" ||
      event.key === "Escape"
    ) {
      setTimeout(() => {
        buttonRef.current?.focus();
      });
    }
  };

  const title = (
    <div ref={refLabel}>
      <Info className={classes.infoIcon} />
      <Typography
        m={0}
        mb={2}
        variant="body2"
        component="p"
        color="grey"
        tone={800}
      >
        {titleProp}
      </Typography>
      <Button
        className={classes.closeButton}
        color="primary"
        variant="text"
        onClick={handleClose}
        onKeyDown={handleKeyDownClose}
        noPadding
        endIcon={<ShortRight />}
        autoFocus
      >
        {buttonText}
      </Button>
      <a
        href="#"
        aria-hidden="true"
        className={classes.hideLinkToMakeFocusBackToIcon}
      ></a>
    </div>
  );

  const buttonClassName = classNames(className, classes.button, {
    [classes.buttonSmall]: size === "small",
    [classes.buttonTiny]: size === "tiny",
  });

  const iconClassName = classNames(classes.icon, {
    [classes.iconSmall]: size === "small",
    [classes.iconTiny]: size === "tiny",
  });

  return (
    <ClickAwayListener onClickAway={handleCloseAway}>
      <Tooltip
        arrow
        classes={tooltipClasses}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        disableInteractive={false}
        open={open}
        placement={placement}
        title={title}
        PopperProps={{
          modifiers,
        }}
      >
        <button
          className={buttonClassName}
          onClick={handleOpen}
          ref={buttonRef}
          type="button"
        >
          {toolTipIcon || <Info2Outline className={iconClassName} />}
        </button>
      </Tooltip>
    </ClickAwayListener>
  );
}

export default InfoTooltip;
